import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import CategoryFilter from '../components/CategoryFilter.jsx';
import RecommendedSuppliers from '../components/RecommendedSuppliers.jsx';
import ProductCard from '../components/ProductCard.jsx';
import Pagination from '../components/Pagination.jsx';

export default function WotsMarketplacePage({ onViewProduct, user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, allowedCategories, onLogout, savedItems, onToggleSave }) {
    const navigate = useNavigate();
    // This state was likely in a child component (ProductListPage), but is needed here
    // to pass the selected category to the new RecommendedSuppliers component.
    const [products, setProducts] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Initialize selection from URL
    const [selectedCategory, setSelectedCategory] = React.useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('category') || 'all';
    });

    // Sync state with URL changes
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const catFromUrl = params.get('category') || 'all';
        if (catFromUrl !== selectedCategory) {
            setSelectedCategory(catFromUrl);
            setCurrentPage(1);
        }
    }, [location.search]);

    // --- NEW: Facet State ---
    const [facets, setFacets] = React.useState({
        brand: '',
        minPrice: '',
        maxPrice: ''
    });

    const [availableFacets, setAvailableFacets] = React.useState({
        brands: {},
        categories: {}
    });

    const [sortOption, setSortOption] = React.useState('date_desc');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);
    const [stickyHeight, setStickyHeight] = React.useState(0); // NEW: State for dynamic sticky height

    const apiEndpoint = "/api/wots_products.php";
    const pageTitle = "Components and Spares";
    const pageSubtitle = "Find components and spares for your equipment";

    const fetchProducts = React.useCallback(() => {
        setLoading(true);
        const url = new URL(apiEndpoint, window.location.origin);
        url.searchParams.append('category', selectedCategory);
        url.searchParams.append('sort', sortOption);
        url.searchParams.append('search', searchTerm);
        // Append Facets
        if (facets.brand) url.searchParams.append('brand', facets.brand);
        if (facets.minPrice) url.searchParams.append('min_price', facets.minPrice);
        if (facets.maxPrice) url.searchParams.append('max_price', facets.maxPrice);

        url.searchParams.append('page', currentPage);
        url.searchParams.append('limit', 50);
        if (allowedCategories && allowedCategories.length > 0) {
            url.searchParams.append('main_categories', allowedCategories.join(','));
        }

        fetch(url, { credentials: 'include' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                return res.json();
            })
            .then(data => {
                setProducts(data.products);
                setTotalPages(data.pagination.totalPages);
                setTotalItems(data.pagination.totalItems);
                if (data.facets) setAvailableFacets(data.facets);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [selectedCategory, sortOption, searchTerm, currentPage, allowedCategories, facets]);

    const fetchCategories = React.useCallback(() => {
        fetch(`/api/categories.php?t=${new Date().getTime()}`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => setError(err.message));
    }, []);

    React.useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    React.useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleCategoryChange = (categorySlug) => {
        setSelectedCategory(categorySlug);
        setCurrentPage(1);
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleSortChange = (sort) => {
        setSortOption(sort);
        setCurrentPage(1);
    };

    const handleSupplierClick = (supplierName, url) => {
        try {
            const formData = new FormData();
            formData.append('supplier_name', supplierName);
            navigator.sendBeacon('/api/track_supplier_click.php', formData);
        } catch (e) {
            console.error("Could not track supplier click:", e);
        }

        // Validate URL exists and is not empty
        if (!url || url.trim() === '') {
            console.error('Supplier link is missing or empty');
            return;
        }

        // Ensure URL has a protocol for external links
        let finalUrl = url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            // If it starts with /, treat as internal route
            if (url.startsWith('/')) {
                const path = url.replace(window.location.origin, '').replace('/miners-market-main', '');
                navigate(path);
                return;
            }
            // Otherwise, assume it's a domain and add https://
            finalUrl = 'https://' + url;
        }

        // Check if it's an internal link
        if (finalUrl.startsWith(window.location.origin) || finalUrl.startsWith('/')) {
            const path = finalUrl.replace(window.location.origin, '').replace('/miners-market-main', '');
            navigate(path);
        } else {
            // External link - use iOS/WKWebView-safe opener
            openExternalLink(finalUrl);
        }
    };

    const handleFacetChange = (key, value) => {
        setFacets(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleClearAll = () => {
        setFacets({brand:'', minPrice:'', maxPrice:''});
        setSearchTerm('');
        setSelectedCategory('all');
        setCurrentPage(1);
    };

    return (
        <div data-name="wots-marketplace-page" data-file="pages/WotsMarketplacePage.js" className="min-h-screen">
            <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={handleCategoryChange}
                searchTerm={searchTerm}
                setSearchTerm={handleSearchChange}
                pageTitle={pageTitle}
                pageSubtitle={pageSubtitle}
                allowedCategories={allowedCategories}
                availableFacets={availableFacets}
                facets={facets}
                onFacetChange={handleFacetChange}
                onClearAll={handleClearAll}
                user={user}
                onShowLogin={onShowLogin}
                onLogout={onLogout}
                basketCount={basket ? basket.reduce((acc, item) => acc + item.quantity, 0) : 0}
                savedItems={savedItems}
                sortOption={sortOption}
                setSortOption={handleSortChange}
                totalItems={totalItems}
                onStickyHeightChange={setStickyHeight} // NEW: Pass callback to update sticky height
            />

            <div className="container mx-auto px-4 pb-4" style={{ paddingTop: '30px' }}> {/* NEW: Fixed padding-top */}
                <RecommendedSuppliers
                    selectedCategory={selectedCategory}
                    allCategories={categories}
                    onSupplierClick={handleSupplierClick}
                />

                <div className="product-grid-container-mobile">
                    {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>}
                    {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}
                    {!loading && !error && products.length === 0 && <div className="text-center py-10 text-gray-500"><p className="text-lg">No products found.</p><p>Try adjusting your search or category filters.</p></div>}
                    {!loading && products.length > 0 && (
                        <div className="product-grid-mobile grid grid-cols-2 md:border-0 md:grid-cols-3 xl:grid-cols-4 md:divide-x-0 md:divide-y-0 md:gap-6">
                            {products.map(product => (
                                <ProductCard 
                                    key={`${product.listingType}-${product.id}`} 
                                    product={product} 
                                    badgeType="wots" 
                                    onViewDetails={() => onViewProduct({ ...product, badgeType: 'wots' })} 
                                    user={user}
                                    basket={basket}
                                    onAddToBasket={onAddToBasket}
                                    onUpdateBasketQuantity={onUpdateBasketQuantity}
                                    onRemoveFromBasket={onRemoveFromBasket}
                                    onShowLogin={onShowLogin}
                                    savedItems={savedItems}
                                    onToggleSave={onToggleSave}
                                />
                            ))}
                        </div>
                    )}
                    {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
                </div>
            </div>
        </div>
    );
}
